import { useState, useEffect } from 'react';
import { Container, Stack, Card, ThemeIcon, Text, Group, Button, Modal, Box, Skeleton } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconX, IconPackage, IconPlus } from '@tabler/icons-react';
import { ExpectedPackagesDesktop } from '../components/package/ExpectedPackagesDesktop';
import { ExpectedPackagesMobile } from '../components/package/ExpectedPackagesMobile';
import { AddExpectedPackageModal } from '../components/package/AddExpectedPackageModal';
import { PackageService } from '../types/services/PackageService';
import { PageTitle } from '../components/common/PageTitle';

// Define the response type to match the API response
interface User {
  id: string;
  userId: string;
  company_name: string;
  first_name: string;
  last_name: string;
  tel: string;
  email: string;
}

interface PackageItem {
  id: number;
  user_id: string;
  invoice_image: string;
  note: string;
  isReceived: boolean;
  created_at: string;
  updated_at: string;
  user: User;
}


const ExpectedPackages = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [packages, setPackages] = useState<PackageItem[]>([]);
  const [, setPagination] = useState({ total: 0, page: 1, limit: 10, pages: 1 });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);

  // Status color mapping for consistent UI
  const statusColors = {
    'pending': 'yellow',
    'in-transit': 'blue',
    'arrived': 'green',
    'received': 'teal'
  };

  // Function to get background style based on status
  const getCardStyle = (isReceived: boolean) => {
    if (isReceived) {
      return {
        backgroundColor: 'var(--mantine-color-gray-0)',
        opacity: 0.7,
        filter: 'grayscale(0.5)'
      };
    }
    return {};
  };

  // Function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status from isReceived
  const getPackageStatus = (isReceived: boolean) => {
    return isReceived ? 'received' : 'in-transit';
  };

  // Fetch function that uses PackageService
  const fetchExpectedPackages = async (page: number = 1) => {
    setIsLoading(true);
    try {
      const response: any = await PackageService.getPackageExpect(undefined, page);

      if (response) {
        // Sort packages - packages with isReceived: false come first
        const sortedPackages = [...response.data].sort((a, b) => {
          if (a.isReceived === b.isReceived) return 0;
          return a.isReceived ? 1 : -1; // isReceived: true goes to the bottom
        });

        setPackages(sortedPackages);
        setPagination(response.pagination);
      }
    } catch (error) {
      showNotification({
        title: 'Error',
        message: 'Failed to fetch expected packages',
        color: 'red',
        icon: <IconX />
      });
      setPackages([]);
    } finally {
      setIsLoading(false);
    }
  };


  useEffect(() => {
    fetchExpectedPackages();
  }, []);

  // // Handle delete modal open
  // const openDeleteModal = (id: number) => {
  //   setPackageToDelete(id);
  //   setDeleteModalOpen(true);
  // };

  // Handle delete package
  const handleDeletePackage = async () => {
    if (!packageToDelete) return;

    setIsDeleting(true);
    try {
      // await PackageService.deletePackageExpect(packageToDelete);

      // Update local state to remove the deleted package
      setPackages(packages.filter(pkg => pkg.id !== packageToDelete));

      showNotification({
        title: 'Success',
        message: 'Package successfully deleted',
        color: 'green',
      });

      setDeleteModalOpen(false);
    } catch (error) {
      showNotification({
        title: 'Error',
        message: 'Failed to delete package',
        color: 'red',
        icon: <IconX />
      });
    } finally {
      setIsDeleting(false);
      setPackageToDelete(null);
    }
  };

  return (
    <Container size="xl" p={{ base: 'xs', sm: 'xl' }}>
      <Stack styles={{ root: { gap: 'var(--mantine-spacing-md)', '@media (min-width: 768px)': { gap: 'var(--mantine-spacing-xl)' } } }}>
        <PageTitle
          title="Expected Packages"
          description="Track packages you're expecting to arrive at our warehouse."
          actions={
            <>
              <Box visibleFrom="lg">
                <Button
                  leftSection={<IconPlus size={16} />}
                  style={{ minWidth: 120 }}
                  fullWidth={false}
                  onClick={() => setAddModalOpen(true)}
                  variant="outline"
                  radius="xl"
                  color="blue"
                >
                  Add Package
                </Button>
              </Box>
              <Box hiddenFrom="lg">
                <ThemeIcon
                  size="lg"
                  radius="xl"
                  variant="light"
                  onClick={() => setAddModalOpen(true)}
                  style={{ cursor: 'pointer' }}
                >
                  <IconPlus size={24} />
                </ThemeIcon>
              </Box>
            </>
          }
        />

        {isLoading ? (
          <>
            <Box hiddenFrom="sm">
              <Stack gap="xs">
                {[1, 2, 3].map((i) => (
                  <Card key={i} withBorder p="xs" radius="md">
                    <Group gap="sm" wrap="nowrap">
                      <Skeleton circle height={40} />
                      <Stack gap={4} style={{ flex: 1 }}>
                        <Skeleton height={16} width="60%" />
                        <Skeleton height={12} width="40%" />
                      </Stack>
                    </Group>
                  </Card>
                ))}
              </Stack>
            </Box>
            <Box visibleFrom="sm">
              <Stack gap="md">
                {[1, 2, 3].map((i) => (
                  <Card key={i} withBorder p="md" radius="md">
                    <Group gap="lg" align="flex-start">
                      <Skeleton height={140} w={200} radius="md" />
                      <Stack gap="sm" style={{ flex: 1 }}>
                        <Skeleton height={24} width="70%" />
                        <Skeleton height={16} width="40%" />
                      </Stack>
                    </Group>
                  </Card>
                ))}
              </Stack>
            </Box>
          </>
        ) : packages.length > 0 ? (
          <>
            <Box hiddenFrom="sm">
              <ExpectedPackagesMobile
                packages={packages}
                statusColors={statusColors}
                getCardStyle={getCardStyle}
                getPackageStatus={getPackageStatus}
                formatDate={formatDate}
              />
            </Box>
            <Box visibleFrom="sm">
              <ExpectedPackagesDesktop
                packages={packages}
                statusColors={statusColors}
                getCardStyle={getCardStyle}
                getPackageStatus={getPackageStatus}
                formatDate={formatDate}
              />
            </Box>
          </>
        ) : (
          <Card withBorder p="md" ta="center" radius="md">
            <Stack align="center" gap="md">
              <ThemeIcon size={48} radius={100} c="blue" variant="light">
                <IconPackage style={{ width: '60%', height: '60%' }} />
              </ThemeIcon>
              <div>
                <Text fw={500} size="lg">No expected packages</Text>
                <Text c="dimmed" size="sm" maw={400} mx="auto" mt={4}>
                  You don't have any expected packages at the moment.
                </Text>
              </div>
            </Stack>
          </Card>
        )}
      </Stack>

      <AddExpectedPackageModal
        opened={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={() => {
          fetchExpectedPackages();
          showNotification({
            title: 'Success',
            message: 'Package added successfully',
            color: 'green'
          });
        }}
      />

      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Expected Package"
        centered
        size="95%"
        padding="sm"
      >
        <Stack>
          <Text size="sm">
            Are you sure you want to delete this expected package? This action cannot be undone.
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)} size="sm">
              Cancel
            </Button>
            <Button color="red" onClick={handleDeletePackage} loading={isDeleting} size="sm">
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};

export default ExpectedPackages;
